import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search, BarChart3, Share2, Mail, MapPin, Store, Users,
  ArrowRight, TrendingUp, Palette, Workflow, MessageSquare, Megaphone,
  Target, Sparkles,
} from "lucide-react";
import { PageShell, PageHero } from "@/components/site/page-shell";
import { CTABanner } from "@/components/sections/cta-banner";
import { TopRelatedDigitalItems } from "@/components/sections/top-related-digital-items";
import { Input } from "@/components/ui/input";

export const Route = createFileRoute("/marketing")({
  component: MarketingHub,
  head: () => ({
    meta: [
      { title: "Digital Marketing Services — SEO, Ads, Social, Email — CoderLity" },
      { name: "description", content: "Full-service digital marketing — SEO, PPC, social, email, WhatsApp, CRO, analytics and branding to grow your revenue." },
      { property: "og:title", content: "Digital Marketing Services — CoderLity" },
      { property: "og:description", content: "SEO, PPC, social, email, WhatsApp, CRO, analytics and branding — all under one roof." },
      { property: "og:url", content: "/marketing" },
    ],
    links: [{ rel: "canonical", href: "https://coderlity.com/marketing" }],
  }),
});

type ChildKind = "child" | "slug";
type Child = { slug: string; title: string };
type Category = {
  slug: string;
  icon: typeof Search;
  title: string;
  tagline: string;
  childParam: ChildKind; // most use `$child`, local-seo/amazon/lead-generation use `$slug`
  groups: { label: string; items: Child[] }[];
};

const CATEGORIES: Category[] = [
  {
    slug: "search-seo",
    icon: Search,
    title: "Search & SEO",
    tagline: "Win organic traffic that compounds over time.",
    childParam: "child",
    groups: [
      { label: "SEO", items: [
        { slug: "technical-seo", title: "Technical SEO" },
        { slug: "on-page-seo", title: "On-page SEO" },
        { slug: "link-building", title: "Link Building" },
      ]},
      { label: "Content", items: [
        { slug: "keyword-research", title: "Keyword Research" },
        { slug: "blog-articles", title: "Blog & Articles" },
        { slug: "ai-content", title: "AI Content" },
      ]},
    ],
  },
  {
    slug: "paid-advertising",
    icon: BarChart3,
    title: "Paid Advertising",
    tagline: "Performance ads across every major channel.",
    childParam: "child",
    groups: [
      { label: "Channels", items: [
        { slug: "google-ads", title: "Google Ads" },
        { slug: "meta-ads", title: "Meta Ads" },
        { slug: "linkedin-ads", title: "LinkedIn Ads" },
        { slug: "tiktok-ads", title: "TikTok Ads" },
      ]},
      { label: "Optimization", items: [
        { slug: "landing-pages", title: "Landing Pages" },
        { slug: "ab-testing", title: "A/B Testing" },
        { slug: "tracking-pixels", title: "Tracking & Pixels" },
      ]},
    ],
  },
  {
    slug: "social-content",
    icon: Share2,
    title: "Social & Content",
    tagline: "Build a brand people actually care about.",
    childParam: "child",
    groups: [
      { label: "Social", items: [
        { slug: "social-strategy", title: "Social Strategy" },
        { slug: "community-management", title: "Community Management" },
        { slug: "influencer-marketing", title: "Influencer Marketing" },
      ]},
      { label: "Creative", items: [
        { slug: "video-production", title: "Video Production" },
        { slug: "graphic-design", title: "Graphic Design" },
        { slug: "brand-storytelling", title: "Brand Storytelling" },
      ]},
    ],
  },
  {
    slug: "email-automation",
    icon: Mail,
    title: "Email & Automation",
    tagline: "Lifecycle campaigns that nurture and convert.",
    childParam: "child",
    groups: [
      { label: "Email", items: [
        { slug: "email-campaigns", title: "Email Campaigns" },
        { slug: "newsletter-strategy", title: "Newsletter Strategy" },
        { slug: "transactional-email", title: "Transactional Email" },
      ]},
      { label: "Automation", items: [
        { slug: "marketing-automation", title: "Marketing Automation" },
        { slug: "crm-integration", title: "CRM Integration" },
        { slug: "lead-scoring", title: "Lead Scoring" },
      ]},
    ],
  },
  {
    slug: "local-seo",
    icon: MapPin,
    title: "Local SEO",
    tagline: "Dominate local search and map results.",
    childParam: "slug",
    groups: [
      { label: "Google Business", items: [
        { slug: "gmb-setup", title: "GMB Setup" },
        { slug: "gmb-verification", title: "GMB Verification" },
        { slug: "gmb-optimization", title: "GMB Optimization" },
        { slug: "gmb-ranking", title: "GMB Ranking" },
        { slug: "gmb-reinstatement", title: "GMB Reinstatement" },
      ]},
      { label: "Local Presence", items: [
        { slug: "google-maps-seo", title: "Google Maps SEO" },
        { slug: "citation-building", title: "Citation Building" },
        { slug: "local-listings", title: "Local Listings" },
        { slug: "map-pack-ranking", title: "Map Pack Ranking" },
      ]},
      { label: "Reputation", items: [
        { slug: "google-reviews", title: "Google Reviews" },
        { slug: "reputation-management", title: "Reputation Management" },
      ]},
    ],
  },
  {
    slug: "amazon",
    icon: Store,
    title: "Amazon Services",
    tagline: "End-to-end Amazon marketplace solutions.",
    childParam: "slug",
    groups: [
      { label: "Account", items: [
        { slug: "amazon-seller-account", title: "Amazon Seller Account" },
        { slug: "amazon-approval", title: "Amazon Approval" },
        { slug: "amazon-verification", title: "Amazon Verification" },
        { slug: "account-reinstatement", title: "Account Reinstatement" },
      ]},
      { label: "Product & SEO", items: [
        { slug: "amazon-seo", title: "Amazon SEO" },
        { slug: "product-listing", title: "Product Listing" },
        { slug: "keyword-research", title: "Keyword Research" },
        { slug: "keyword-ranking", title: "Keyword Ranking" },
        { slug: "product-reviews", title: "Product Reviews" },
      ]},
      { label: "Ads & Logistics", items: [
        { slug: "amazon-ppc", title: "Amazon PPC" },
        { slug: "fba-setup", title: "FBA Setup" },
        { slug: "brand-registry", title: "Brand Registry" },
      ]},
    ],
  },
  {
    slug: "lead-generation",
    icon: Users,
    title: "Lead Generation",
    tagline: "High-intent leads delivered to your pipeline.",
    childParam: "slug",
    groups: [
      { label: "Lead Types", items: [
        { slug: "b2b-leads", title: "B2B Leads" },
        { slug: "b2c-leads", title: "B2C Leads" },
        { slug: "sales-leads", title: "Sales Leads" },
        { slug: "targeted-leads", title: "Targeted Leads" },
        { slug: "email-leads", title: "Email Leads" },
      ]},
      { label: "Outbound", items: [
        { slug: "cold-calling", title: "Cold Calling" },
        { slug: "appointment-setting", title: "Appointment Setting" },
        { slug: "linkedin-leads", title: "LinkedIn Leads" },
        { slug: "facebook-leads", title: "Facebook Leads" },
      ]},
      { label: "Channels & CRM", items: [
        { slug: "whatsapp-marketing", title: "WhatsApp Marketing" },
        { slug: "sms-marketing", title: "SMS Marketing" },
        { slug: "crm-management", title: "CRM Management" },
        { slug: "funnel-marketing", title: "Funnel Marketing" },
        { slug: "landing-page-leads", title: "Landing Page Leads" },
      ]},
    ],
  },
];

type Standalone = { to: string; title: string; desc: string; icon: typeof Search };
const MORE_MARKETING: Standalone[] = [
  { to: "/marketing/content-marketing", title: "Content Marketing", desc: "SEO content, editorial and thought leadership.", icon: Palette },
  { to: "/marketing/conversion-optimization", title: "Conversion Optimization", desc: "A/B tests and CRO programs that lift revenue.", icon: TrendingUp },
  { to: "/marketing/analytics-tracking", title: "Analytics & Tracking", desc: "GA4, GTM, server-side tracking and dashboards.", icon: BarChart3 },
  { to: "/marketing/branding-creative-design", title: "Branding & Creative", desc: "Identity, ad creative and design systems.", icon: Sparkles },
  { to: "/marketing/marketing-automation", title: "Marketing Automation", desc: "Workflows and journeys that scale your team.", icon: Workflow },
  { to: "/marketing/whatsapp-sms-marketing", title: "WhatsApp & SMS", desc: "Broadcast and 2-way conversational messaging.", icon: MessageSquare },
  { to: "/marketing/email-marketing", title: "Email Marketing", desc: "Broadcasts, flows and lifecycle campaigns.", icon: Mail },
  { to: "/marketing/google-ads-ppc", title: "Google Ads & PPC", desc: "Profitable Google Search & Performance Max.", icon: Target },
  { to: "/marketing/social-media-marketing", title: "Social Media Marketing", desc: "Organic + paid social that converts.", icon: Megaphone },
];

function hrefFor(cat: Category, childSlug: string) {
  return `/marketing/${cat.slug}/${childSlug}`;
}

function MarketingHub() {
  const [activeSlug, setActiveSlug] = useState<string>(CATEGORIES[0].slug);
  const [query, setQuery] = useState("");

  const active = CATEGORIES.find((c) => c.slug === activeSlug) ?? CATEGORIES[0];

  const searchResults = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return null;
    const results: { to: string; title: string; parent: string }[] = [];
    for (const c of CATEGORIES) {
      if (c.title.toLowerCase().includes(q)) {
        results.push({ to: `/marketing/${c.slug}`, title: c.title, parent: "Category" });
      }
      for (const g of c.groups) {
        for (const it of g.items) {
          if (it.title.toLowerCase().includes(q)) {
            results.push({ to: hrefFor(c, it.slug), title: it.title, parent: c.title });
          }
        }
      }
    }
    for (const s of MORE_MARKETING) {
      if (s.title.toLowerCase().includes(q) || s.desc.toLowerCase().includes(q)) {
        results.push({ to: s.to, title: s.title, parent: "Specialized" });
      }
    }
    return results.slice(0, 20);
  }, [query]);

  return (
    <PageShell>
      <PageHero
        eyebrow="Marketing"
        title="Growth marketing that pays for itself"
        sub="Pick a channel — or search across SEO, ads, social, email, local and lead gen."
      />

      {/* Search bar */}
      <section className="pt-2">
        <div className="mx-auto max-w-3xl px-4 lg:px-8">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search marketing (e.g. SEO, Google Ads, Email)…"
              className="h-12 rounded-full pl-11 pr-4 text-sm"
              aria-label="Search marketing services"
            />
          </div>
          {searchResults && (
            <div className="mt-3 rounded-2xl border border-border bg-card p-2 shadow-card">
              {searchResults.length === 0 ? (
                <div className="px-3 py-4 text-sm text-muted-foreground">No matches. Try another keyword.</div>
              ) : (
                <ul className="max-h-80 overflow-auto">
                  {searchResults.map((r) => (
                    <li key={r.to}>
                      <Link
                        to={r.to as never}
                        className="group flex items-center justify-between rounded-lg px-3 py-2 hover:bg-secondary transition"
                      >
                        <span className="min-w-0 truncate text-sm font-medium">{r.title}</span>
                        <span className="ml-3 shrink-0 text-[11px] uppercase tracking-wider text-muted-foreground">{r.parent}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Interactive explorer: tabs + detail */}
      <section className="py-12">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <div
            className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1"
            role="tablist"
            aria-label="Marketing categories"
          >
            {CATEGORIES.map((c) => {
              const isActive = c.slug === activeSlug;
              const Icon = c.icon;
              return (
                <button
                  key={c.slug}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setActiveSlug(c.slug)}
                  className={
                    "group shrink-0 inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition " +
                    (isActive
                      ? "border-primary/40 bg-primary/10 text-primary shadow-sm"
                      : "border-border bg-card text-muted-foreground hover:border-primary/30 hover:text-foreground")
                  }
                >
                  <Icon className="h-4 w-4 shrink-0" />
                  <span className="truncate">{c.title}</span>
                </button>
              );
            })}
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={active.slug}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.2 }}
              className="mt-6 rounded-3xl border border-border bg-card overflow-hidden"
            >
              <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-4 border-b border-border/70 bg-muted/30 p-5 sm:p-6 sm:flex sm:items-center sm:justify-between">
                <div className="flex min-w-0 items-start gap-4">
                  <div className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary">
                    <active.icon className="h-5 w-5" />
                  </div>
                  <div className="min-w-0">
                    <h2 className="truncate text-xl sm:text-2xl font-bold tracking-tight">{active.title}</h2>
                    <p className="mt-0.5 text-sm text-muted-foreground line-clamp-2">{active.tagline}</p>
                  </div>
                </div>
                <Link
                  to={`/marketing/${active.slug}` as never}
                  className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-gradient-brand px-4 py-2 text-xs font-semibold text-white shadow-glow"
                >
                  Overview <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>

              <div className="p-5 sm:p-6 grid gap-6 md:grid-cols-3">
                {active.groups.map((g) => (
                  <div key={g.label}>
                    <h3 className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">{g.label}</h3>
                    <ul className="mt-2 space-y-1.5">
                      {g.items.map((it) => (
                        <li key={it.slug}>
                          <Link
                            to={hrefFor(active, it.slug) as never}
                            className="group flex items-center justify-between rounded-lg border border-transparent px-3 py-2 hover:border-border hover:bg-secondary/60 transition"
                          >
                            <span className="min-w-0 truncate text-sm font-medium">{it.title}</span>
                            <ArrowRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground transition group-hover:translate-x-0.5 group-hover:text-primary" />
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      {/* Specialized standalone marketing services */}
      <section className="pb-14">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <div className="mb-6 flex items-end justify-between gap-4">
            <div>
              <h2 className="text-xl sm:text-2xl font-bold tracking-tight">Specialized practices</h2>
              <p className="mt-1 text-sm text-muted-foreground">Standalone offerings — engage on their own or bundle with any category above.</p>
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {MORE_MARKETING.map((s) => (
              <Link
                key={s.to}
                to={s.to as never}
                className="group flex items-start gap-4 rounded-2xl border border-border bg-card p-5 hover:border-primary/40 transition"
              >
                <div className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary">
                  <s.icon className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="text-sm font-semibold">{s.title}</h3>
                  <p className="mt-1 text-xs text-muted-foreground line-clamp-2">{s.desc}</p>
                </div>
                <ArrowRight className="mt-1 h-4 w-4 shrink-0 text-muted-foreground transition group-hover:translate-x-0.5 group-hover:text-primary" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Custom CTA */}
      <section className="pb-14">
        <div className="mx-auto max-w-5xl px-4 lg:px-8">
          <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-6 rounded-3xl border border-border bg-gradient-to-br from-card to-muted/40 p-6 sm:p-8 sm:flex sm:justify-between">
            <div className="min-w-0">
              <h3 className="text-lg sm:text-xl font-bold tracking-tight">Not sure which channel fits?</h3>
              <p className="mt-1 text-sm text-muted-foreground">Tell us your goals — we'll map a channel mix and forecast ROI in 24 hours.</p>
            </div>
            <Link
              to="/contact"
              className="shrink-0 inline-flex items-center gap-2 rounded-full bg-gradient-brand px-5 py-2.5 text-sm font-semibold text-white shadow-glow"
            >
              Get a free growth audit <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <TopRelatedDigitalItems context="marketing" />

      <CTABanner />
    </PageShell>
  );
}
